import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Rocket, Headphones, Code, LayoutTemplate } from 'lucide-react';
import { projectsData } from '../data/projectsData';
import SEO from '../components/SEO';
import { getBreadcrumbSchema } from '../data/schemas';

const Projects = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', 'Web Applications', 'Bots', 'Research & AI', 'Hardware & C++'];

  const filteredProjects = projectsData.filter((project) => {
    const matchesCategory = selectedCategory === 'All' || project.category === selectedCategory;
    const matchesSearch = 
      project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.tech.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'View >':
        return (
          <span className="text-xs font-mono font-bold px-3 py-1 bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40 rounded-full flex items-center gap-1 group-hover:bg-fuchsia-600 group-hover:text-white transition-all shadow-sm">
            View &gt;
          </span>
        );
      case 'Active':
      case 'Deployed':
        return (
          <span className="text-xs font-mono px-3 py-1 bg-purple-950/60 text-fuchsia-300 border border-purple-500/40 rounded-full flex items-center gap-1.5 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-fuchsia-400 animate-pulse" />
            {status}
          </span>
        );
      case 'In Development':
      default:
        return (
          <span className="text-xs font-mono px-3 py-1 bg-pink-950/60 text-pink-300 border border-pink-500/40 rounded-full flex items-center gap-1.5 shadow-sm">
            <span className="w-2 h-2 rounded-full bg-pink-400 animate-pulse" />
            {status}
          </span>
        );
    }
  };

  return (
    <div className="animate-fade-in-down max-w-6xl mx-auto space-y-6 sm:space-y-10">
      <SEO
        title="Systems Directory & Software Architecture"
        description="Explore custom software architectures, native iOS biometric applications, 24/7 Discord bots, and full-stack web applications built by NotKoneko."
        path="/projects"
        keywords={['Koneko Projects', 'NotKoneko Systems', 'NeuroSyn', 'Lyrika-Stanza', 'Tamed Web App', 'Website Templates', 'Creator Templates', 'Spotify Controller', 'Swift 6', 'Discord Bot Engine']}
        schema={getBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Projects', url: '/projects' }
        ])}
      />
      {/* Header Banner & Stats Summary */}
      <div className="bg-[#090714] p-5 sm:p-8 border border-purple-500/20 rounded-2xl sm:rounded-3xl shadow-2xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600/10 rounded-full blur-[140px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-pink-600/10 rounded-full blur-[140px] pointer-events-none" />

        <div className="flex flex-wrap items-center justify-between gap-6 relative z-10">
          <div className="space-y-2 max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-950/50 border border-purple-500/30 text-purple-300 text-xs font-mono">
              <Rocket className="w-3.5 h-3.5 text-fuchsia-400" /> Software Architecture &amp; Engineering Showcase
            </div>
            <h1 className="text-3xl sm:text-4xl font-serif text-white tracking-wide">Project Directory</h1>
            <p className="text-gray-300 text-sm sm:text-base leading-relaxed font-serif italic">
              — Explore custom systems, specialized Discord bots, research software models, and custom hardware interface software.
            </p>
          </div>

          {/* Quick Stats Grid */}
          <div className="w-full sm:w-auto grid grid-cols-3 gap-1.5 sm:gap-3 bg-[#0d0a1c] p-3 sm:p-4 rounded-2xl border border-purple-500/20 font-mono text-center shadow-lg">
            <div className="px-2 sm:px-3 py-1.5 sm:py-2 border-r border-purple-500/20">
              <span className="block text-xl sm:text-2xl font-extrabold text-fuchsia-300 tracking-tight">{projectsData.length}</span>
              <span className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-wider block mt-0.5">Systems</span>
            </div>
            <div className="px-2 sm:px-3 py-1.5 sm:py-2 border-r border-purple-500/20">
              <span className="block text-xl sm:text-2xl font-extrabold text-pink-300 tracking-tight">2</span>
              <span className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-wider block mt-0.5">Bots</span>
            </div>
            <div className="px-2 sm:px-3 py-1.5 sm:py-2">
              <span className="block text-xl sm:text-2xl font-extrabold text-purple-200 tracking-tight">100%</span>
              <span className="text-[9px] sm:text-[10px] text-gray-400 uppercase tracking-wider block mt-0.5">Custom</span>
            </div>
          </div>
        </div>
      </div>

      {/* Interactive Controls: Search & Category Filters */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 bg-[#090714] p-3.5 sm:p-4 rounded-2xl border border-purple-500/20 shadow-md">
        {/* Category Tabs */}
        <div className="flex flex-wrap items-center justify-start gap-1.5 sm:gap-2 w-full md:w-auto">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 text-[11px] sm:text-xs font-mono font-bold rounded-xl transition-all ${
                selectedCategory === cat
                  ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg shadow-purple-500/25 scale-105 border border-purple-400/30'
                  : 'bg-[#0d0a1c] text-gray-400 hover:text-fuchsia-300 hover:bg-purple-950/40 border border-purple-500/20'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Search Bar Input */}
        <div className="relative w-full md:w-72">
          <input
            type="text"
            placeholder="Search projects or tech..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-[#0d0a1c] border border-purple-500/20 text-xs text-gray-200 placeholder-gray-500 rounded-xl px-4 py-2.5 focus:outline-none focus:border-fuchsia-500/60 transition-colors"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-2.5 text-gray-500 hover:text-fuchsia-300 text-xs font-mono"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Projects Cards Grid */}
      {filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
          {filteredProjects.map((project) => (
            <Link 
              key={project.id} 
              to={`/projects/${project.id}`} 
              className="group block bg-[#090714] hover:bg-[#0e0a1a] p-5 sm:p-7 border border-purple-500/20 hover:border-fuchsia-500/50 hover:shadow-[0_0_25px_rgba(232,121,249,0.15)] rounded-2xl transition-all duration-300 transform hover:-translate-y-1.5 shadow-xl relative overflow-hidden flex flex-col justify-between"
            >
              {/* Subtle hover accent line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-fuchsia-500/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

              <div>
                {/* Top Card Bar */}
                <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-3">
                  <div className="flex items-center gap-3">
                    {project.icon === 'discord' ? (
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-[#5865F2]/15 border border-[#5865F2]/40 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-lg shadow-[#5865F2]/15">
                        <svg className="w-6 h-6 fill-[#5865F2]" viewBox="0 0 127.14 96.36">
                          <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1,105.25,105.25,0,0,0,32.19-16.14c2.64-27.38-4.51-51.11-18.91-72.15ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,45.91,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,45.91,96.12,53,91.08,65.69,84.69,65.69Z"/>
                        </svg>
                      </div>
                    ) : project.icon === 'tamed' ? (
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl overflow-hidden shrink-0 group-hover:scale-110 transition-transform">
                        <img src="/tamed-icon.webp" alt="Tamed Icon" className="w-full h-full object-contain rounded-xl" />
                      </div>
                    ) : project.icon === 'neurosyn' || project.icon === 'iphone' ? (
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl overflow-hidden shrink-0 group-hover:scale-110 transition-transform">
                        <img src="/neurosyn-icon.png" alt="NeuroSyn Icon" className="w-full h-full object-contain rounded-xl" />
                      </div>
                    ) : project.icon === 'spotify' ? (
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                        <Headphones className="w-5 h-5 sm:w-6 sm:h-6 text-fuchsia-400" />
                      </div>
                    ) : project.icon === 'templates' ? (
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-tr from-fuchsia-500/15 via-purple-600/15 to-indigo-600/15 border border-fuchsia-500/30 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform shadow-lg shadow-fuchsia-500/10">
                        <LayoutTemplate className="w-5 h-5 sm:w-6 sm:h-6 text-fuchsia-300" />
                      </div>
                    ) : (
                      <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-purple-500/10 border border-purple-500/30 flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform">
                        <Code className="w-5 h-5 sm:w-6 sm:h-6 text-purple-300" />
                      </div>
                    )}
                    <div>
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="text-[10px] font-mono text-purple-400/90 uppercase tracking-wider block">
                          {project.category || 'Engineering Project'}
                        </span>
                        {project.isCommission && (
                          <span className="px-2 py-0.5 bg-purple-950/90 text-fuchsia-300 border border-purple-500/50 rounded-md font-mono font-bold text-[9px] tracking-wider shadow-sm">
                            CLIENT COMMISSION
                          </span>
                        )}
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-white group-hover:text-fuchsia-300 transition-colors">
                        {project.title}
                      </h3>
                    </div>
                  </div>
                  {getStatusBadge(project.status)}
                </div>

                {/* Description */}
                <p className="text-gray-300 text-sm leading-relaxed mb-6">
                  {project.description}
                </p>

                {/* Highlight Badges Checklist */}
                {project.highlights && (
                  <div className="mb-6 bg-[#0d0a1c] p-3.5 rounded-xl border border-purple-500/20">
                    <span className="text-[10px] font-mono text-purple-400/90 uppercase tracking-wider block mb-2">Key Specifications</span>
                    <div className="flex flex-wrap gap-2">
                      {project.highlights.map((h, i) => (
                        <span key={i} className="text-[11px] font-mono px-2.5 py-1 bg-purple-950/40 text-purple-200 rounded-md border border-purple-500/20">
                          • {h}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Card Footer */}
              <div className="pt-4 border-t border-purple-500/20 flex items-center justify-between text-xs font-mono mt-2">
                <span className="text-fuchsia-400">{project.tech}</span>
                <span className="text-gray-400 group-hover:text-fuchsia-300 font-semibold flex items-center gap-1 transition-colors">
                  Explore <span className="transform group-hover:translate-x-1 transition-transform">→</span>
                </span>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="bg-[#090714] p-12 border border-purple-500/20 rounded-2xl text-center text-gray-400 font-mono">
          No systems found matching your criteria.
        </div>
      )}
    </div>
  );
};

export default Projects;